import React, { useCallback, useEffect, useState } from 'react';
import {
  ChevronDownIcon,
  MagnifyingGlassIcon,
  PhoneArrowUpRightIcon,
} from '@heroicons/react/24/outline';
import { useSearchParams } from 'react-router-dom';
import api, { getErrorMessage } from '../../../services/api';
import { SESSION_STATUS } from '../../../stores/callSessionStore';

/**
 * A quién voy a llamar después, y a quién quiero llamar YA.
 *
 * Las dos mitades de lo mismo, pedido por Edgar el 07/10/2026 llamando: hasta
 * hoy el gestor marcaba completamente a ciegas — ni sabía quién venía después,
 * ni podía adelantar a alguien concreto.
 *
 * ## Por qué esto no contradice el diseño de la pantalla
 *
 * `CallSessionPage` dice, con razón, que **el gestor no elige a quién llamar**:
 * elegir cuesta segundos de los 390 minutos útiles, y el score acierta más que
 * la intuición. Eso sigue en pie para la jornada normal. Esto es la **excepción
 * deliberada**: el cliente que avisa de que va a pagar, el deudor que pidió que
 * le llamaran a las cinco. Por eso el buscador **solo funciona en pausa** — para
 * usarlo hay que salirse del flujo a propósito, no por despiste.
 *
 * ## 🔴 «Orden aproximado» no es un adorno
 *
 * El marcador **explora**: una de cada veinte veces coge un candidato al azar en
 * vez del mejor, para que la cola siga aprendiendo de casos que el score nunca
 * elegiría. El endpoint devuelve el orden determinista, pero cuando marque de
 * verdad puede no respetarlo. Si la pantalla lo presentara como una promesa, el
 * gestor vería que el sistema le miente cada pocas llamadas.
 */

/** Cada cuánto se refresca la cola mientras está abierta. */
const REFRESCO_MS = 30000;

/** Por debajo de esto no se busca: tres letras devuelven media base. */
const MINIMO_BUSQUEDA = 3;

const euros = (n) =>
  typeof n === 'number'
    ? n.toLocaleString('es-ES', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 })
    : '—';

const COLOR_PRIORIDAD = {
  urgent: 'bg-red-100 text-red-700',
  high: 'bg-orange-100 text-orange-700',
  medium: 'bg-gray-100 text-gray-600',
  low: 'bg-gray-100 text-gray-500',
};

const Fila = ({ children }) => (
  <div className='flex items-center justify-between gap-3 border-b border-gray-100 py-2 last:border-0'>
    {children}
  </div>
);

const ColaYBuscador = ({ status }) => {
  // La ficha del expediente manda aquí con el deudor ya buscado (?buscar=), para
  // llamarle en AR con el número del gestor (docs/contactar-deudor-ar-plan.md).
  const [searchParams] = useSearchParams();
  const buscar = searchParams.get('buscar') || '';
  const [abierto, setAbierto] = useState(Boolean(buscar));
  const [cola, setCola] = useState([]);
  const [cargandoCola, setCargandoCola] = useState(false);

  const [texto, setTexto] = useState(buscar);
  const [resultados, setResultados] = useState([]);
  const [buscando, setBuscando] = useState(false);
  const [aviso, setAviso] = useState(null);
  const [marcando, setMarcando] = useState(null);

  const enPausa = status === SESSION_STATUS.PAUSED;

  const cargarCola = useCallback(async () => {
    setCargandoCola(true);
    try {
      const { data } = await api.get('/calls/queue?limit=10');
      setCola(data.data || []);
    } catch (err) {
      setAviso(getErrorMessage(err));
    } finally {
      setCargandoCola(false);
    }
  }, []);

  // Solo mientras está abierto: `peek` carga por debajo los fallos seguidos de
  // cada candidato y la tabla de contacto, así que no se pide en bucle con el
  // panel plegado.
  useEffect(() => {
    if (!abierto) return undefined;
    cargarCola();
    const t = setInterval(cargarCola, REFRESCO_MS);
    return () => clearInterval(t);
  }, [abierto, cargarCola]);

  // Antirrebote: sin esto cada tecla es una consulta, y la búsqueda barre
  // expedientes y deudores.
  useEffect(() => {
    if (texto.trim().length < MINIMO_BUSQUEDA) {
      setResultados([]);
      return undefined;
    }
    const t = setTimeout(async () => {
      setBuscando(true);
      try {
        const { data } = await api.get(`/calls/search?q=${encodeURIComponent(texto.trim())}`);
        setResultados(data.data || []);
      } catch (err) {
        setAviso(getErrorMessage(err));
      } finally {
        setBuscando(false);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [texto]);

  const llamar = async (expedientId) => {
    setAviso(null);
    setMarcando(expedientId);
    try {
      await api.post('/calls/dial', { expedient_id: expedientId });
      setTexto('');
      setResultados([]);
    } catch (err) {
      // El backend manda el motivo en castellano y es el que hay que enseñar:
      // «pausa la sesión», «a un judicial no se le llama», «sin cupo hoy».
      setAviso(getErrorMessage(err));
    } finally {
      setMarcando(null);
    }
  };

  return (
    <div className='rounded-lg border border-gray-200 bg-white'>
      <button
        type='button'
        onClick={() => setAbierto((v) => !v)}
        className='flex w-full items-center justify-between px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50'
      >
        <span>Próximas llamadas y buscador</span>
        <ChevronDownIcon
          className={`h-4 w-4 transition-transform ${abierto ? 'rotate-180' : ''}`}
          aria-hidden='true'
        />
      </button>

      {abierto && (
        <div className='space-y-4 border-t border-gray-200 p-4'>
          <section>
            <div className='flex items-baseline justify-between'>
              <p className='text-xs font-semibold uppercase tracking-wide text-gray-500'>
                Próximas llamadas
              </p>
              {/*
                🔴 Esto se queda. El marcador explora, así que el orden de abajo
                es el probable, no el que va a ocurrir seguro.
              */}
              <p className='text-xs text-gray-400'>orden aproximado</p>
            </div>

            {cargandoCola && cola.length === 0 ? (
              <p className='py-3 text-sm text-gray-400'>Cargando…</p>
            ) : cola.length === 0 ? (
              <p className='py-3 text-sm text-gray-500'>No queda nada en cola.</p>
            ) : (
              <div className='mt-1' data-testid='cola-proximas'>
                {cola.map((c) => (
                  <Fila key={c.assignmentId}>
                    <div className='min-w-0'>
                      <p className='truncate text-sm text-gray-800'>{c.debtorName || 'Sin nombre'}</p>
                      <p className='truncate text-xs text-gray-500'>
                        {c.company || 'Sin marca'} · {euros(c.amount)}
                        {c.daysOverdue > 0 && ` · vencida hace ${c.daysOverdue} d`}
                      </p>
                    </div>
                    {c.priority && (
                      <span
                        className={`shrink-0 rounded-lg px-2 py-0.5 text-xs ${
                          COLOR_PRIORIDAD[c.priority] || COLOR_PRIORIDAD.medium
                        }`}
                      >
                        {c.priority}
                      </span>
                    )}
                  </Fila>
                ))}
              </div>
            )}
          </section>

          <section className='border-t border-gray-200 pt-4'>
            <p className='text-xs font-semibold uppercase tracking-wide text-gray-500'>
              Llamar a uno concreto
            </p>

            <div className='relative mt-2'>
              <MagnifyingGlassIcon
                className='pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-gray-400'
                aria-hidden='true'
              />
              <input
                type='text'
                value={texto}
                onChange={(e) => setTexto(e.target.value)}
                placeholder='Nombre, NIF o teléfono'
                aria-label='Buscar un expediente para llamar'
                className='w-full rounded-lg border border-gray-300 py-2 pl-9 pr-3 text-sm'
              />
            </div>

            {/*
              El aviso va ANTES de los resultados: si el gestor no está en pausa,
              tiene que saberlo antes de buscar a quién llamar, no después de
              pulsar y que no pase nada.
            */}
            {!enPausa && (
              <p className='mt-2 text-xs text-amber-700'>
                Pausa la sesión para poder llamar a un expediente concreto.
              </p>
            )}

            {aviso && <p className='mt-2 text-sm text-red-600'>{aviso}</p>}

            {buscando && <p className='mt-2 text-sm text-gray-400'>Buscando…</p>}

            {resultados.length > 0 && (
              <div className='mt-2' data-testid='resultados-busqueda'>
                {resultados.map((r) => (
                  <Fila key={r.expedientId}>
                    <div className='min-w-0'>
                      <p className='truncate text-sm text-gray-800'>{r.debtorName || 'Sin nombre'}</p>
                      <p className='truncate text-xs text-gray-500'>
                        {r.nif || 'Sin NIF'} · {r.company || 'Sin marca'} · {euros(r.amount)}
                      </p>
                    </div>
                    <button
                      type='button'
                      onClick={() => llamar(r.expedientId)}
                      disabled={!enPausa || r.judicial || marcando === r.expedientId}
                      title={
                        r.judicial
                          ? 'A un expediente judicial no se le llama'
                          : !enPausa
                            ? 'Pausa la sesión primero'
                            : 'Llamar ahora'
                      }
                      className='flex shrink-0 items-center gap-1 rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-gray-300'
                    >
                      <PhoneArrowUpRightIcon className='h-4 w-4' aria-hidden='true' />
                      {marcando === r.expedientId ? 'Marcando…' : 'Llamar'}
                    </button>
                  </Fila>
                ))}
              </div>
            )}
          </section>
        </div>
      )}
    </div>
  );
};

export default ColaYBuscador;
